// Ordrevarsler (/ordrevarsler) og Trykkvarsler (/trykkvarsler, api="services"), fra flisene i
// kontrollpanelet (vinnsel 29.10 og 30.10).
// Nye meldinger fra kunder på kundeordre øverst, og varsler som er lest de siste 5 dagene under. Kunden skriver på
// /kundeordre/<token>; backend GET /orders/varsler (src/routes/ordreMeldinger.js).
// Ikke i varslingssenteret, som er for leder.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tilbake, Sidetopp } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import formatDateTime from '../hjelpere/formatDateTime.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Rad({ o, ny }) {
  return (
    <li>
      <Link
        to={o.lenke || `/order-details/${o._id}`}
        className={`flex flex-col gap-1 rounded-xl border px-3 py-1.5 text-slate-900 transition focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
          ny ? 'border-amber-300 bg-amber-50 hover:bg-amber-100' : 'border-slate-200 bg-white hover:bg-slate-50'
        }`}
      >
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold">{o.kind === 'trykk' ? 'Trykk' : o.type === 'skiplukk' ? 'Skiplukk' : 'Ordre'} {o.nummer ?? o.ordreid}</span>
          <span className="min-w-0 break-words">{[o.kunde, o.produkt].filter(Boolean).join(' · ')}</span>
          {o.avsluttet && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">Avsluttet</span>}
          <span className="ml-auto text-sm text-slate-700">
            {o.uleste > 0 ? `${o.uleste} ${o.uleste === 1 ? 'ny melding' : 'nye meldinger'} · ` : ''}
            {o.siste ? formatDateTime(o.siste) : ''}
          </span>
        </span>
        {o.sisteTekst && (
          <span className="text-sm text-slate-700 break-words">
            <span className="font-medium">{o.sisteFra === 'kunde' ? 'Kunden' : 'Butikken'}:</span> {o.sisteTekst}
          </span>
        )}
      </Link>
    </li>
  );
}

export default function Ordrevarsler({ api = 'orders' }) {
  const trykk = api === 'services';
  const [data, setData] = useState(null);
  const [feil, setFeil] = useState('');

  useEffect(() => {
    let avbrutt = false;
    const hent = () =>
      fetch(`${API_BASE_URL}/${api}/varsler?alle=1&t=${Date.now()}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
        .then((d) => { if (!avbrutt) { setData(d); setFeil(''); } })
        .catch(() => { if (!avbrutt) setFeil('Kunne ikke hente ordrevarslene.'); });
    hent();
    const id = setInterval(hent, 60000);
    return () => { avbrutt = true; clearInterval(id); };
  }, [api]);

  const ordrer = data?.ordrer || [];
  const nye = ordrer.filter((o) => o.uleste > 0);
  const lest = ordrer.filter((o) => o.uleste === 0);

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake />
      <Sidetopp
        ikon="melding"
        farge={trykk ? 'trykk' : 'betaling'}
        tittel={trykk ? 'Trykkvarsler' : 'Ordrevarsler'}
        undertittel={trykk ? 'Meldinger fra kunder om trykk' : 'Meldinger fra kunder om ordrer og skiplukk'}
      />

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
      {!data && !feil && <p role="status" className="text-slate-700">Henter …</p>}

      {data && (
        <section aria-labelledby="ov-nye" className="space-y-2">
          <h2 id="ov-nye" className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Ikon navn="melding" className="w-6 h-6" /> Nye meldinger fra kunder ({nye.length})
          </h2>
          {nye.length ? (
            <ul className="space-y-2">{nye.map((o) => <Rad key={o._id} o={o} ny />)}</ul>
          ) : (
            <p className="rounded-xl bg-slate-50 px-3 py-1.5 text-slate-700">Ingen nye meldinger.</p>
          )}
        </section>
      )}

      {data && (
        <section aria-labelledby="ov-siste" className="space-y-2">
          <h2 id="ov-siste" className="text-lg font-bold text-slate-900">Lest siste 5 dager</h2>
          <p className="text-sm text-slate-600">Et varsel er lest når {trykk ? 'trykket' : 'ordren'} åpnes eller noen svarer kunden. Det står her i 5 dager etter det.</p>
          {lest.length ? (
            <ul className="space-y-2">{lest.map((o) => <Rad key={o._id} o={o} />)}</ul>
          ) : (
            <p className="rounded-xl bg-slate-50 px-3 py-1.5 text-slate-700">Ingen leste varsler de siste 5 dagene.</p>
          )}
        </section>
      )}
    </div>
  );
}
